/* eslint-disable react/style-prop-object */
import React, { useEffect, useState } from "react";
import { Attention, Button, Checkbox } from "@optimizely/axiom";
import { useForm } from "react-hook-form";

import { Invitation } from "../../../domain/Invitation";
import {
    INVITATION_ERROR_MESSAGE,
    INVITATION_SUCCESS_MESSAGE,
    REFETCH_PERMISSIONS_EVENT_NAME
} from "../../../constants/constants";
import { adaptApiErrors, IApiError } from "../../../services/ErrorMessageAdapter";
import styles from "../InvitationDetails/InvitationDetails.module.scss";
import { SidebarFooter } from "../Sidebar/SidebarFooter";
import { useUserContext } from "../../../providers/UserProvider";
import { useLocation } from "react-router-dom";
import { isValidEmail } from "../../../lib/utils";
import { User } from "../../../domain/User";
import { emitToast } from "../../../lib/toaster-utils";
import { InvitationAccessForm } from "./InvitationAccess/InvitationAccessForm";
import { getGrantPermissionsPayload } from "../../../lib/analytics-helpers";
import { useAnalyticsTracking } from "../../../hooks/useAnalyticsTracking/useAnalyticsTracking";
import { ANALYTICS_FLOWS, ANALYTICS_TRACKED_COMPONENTS } from "../../../constants/analytics-constants";
import { useOrganization } from "../../../hooks/useOrganization/useOrganization";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag/useFeatureFlag";
import { Flags } from "../../../feature-flags/flags";
import { EmailSelectorInput } from "./EmailSelectorInput/EmailSelectorInput";
import { AccessDetailsByProductId } from "../../../domain/AccessDetails";
import { toSelectedUserGroups, UserGroupWithProjectDetails } from "../../../lib/access-helpers";

interface IInvitationFormValues {
    firstName: string;
    lastName: string;
    apiError: { message: string };
}

export const AccessManagementInvitationForm = ({
    onCancel,
    onCreate,
    usersFlow = false
}: {
    onCancel: () => void;
    onCreate: ({
        newInvitation,
        inviteAnother
    }: {
        newInvitation: Invitation | null;
        inviteAnother: boolean;
    }) => Promise<boolean>;
    usersFlow?: boolean;
}) => {
    const { organizationId, profile } = useUserContext();
    const [accessDetailsByProductId, setAccessDetailsByProductId] = useState<AccessDetailsByProductId>({});
    const [groupsWithoutProductAccess, setGroupsWithoutProductAccess] = useState<UserGroupWithProjectDetails[]>([]);
    const [inviteAnotherAccess, setInviteAnotherAccess] = useState<AccessDetailsByProductId | undefined>(undefined);
    const [inviteAnotherGroupsWithoutAccess, setInviteAnotherGroupsWithoutAccess] = useState<
        UserGroupWithProjectDetails[] | undefined
    >(undefined);
    const [selectedUser, setSelectedUser] = useState<User>();
    const [email, setEmail] = useState<string | null>(null);
    const { pathname } = useLocation();
    const { sendTrackEvent } = useAnalyticsTracking();

    const { organization, loading: organizationLoading } = useOrganization({ id: organizationId });
    const { enabled: hideForceAcceptance } = useFeatureFlag(Flags.HIDE_FORCE_ACCEPTANCE_CHECKBOX);
    const showReqAcceptanceChkBox =
        !organizationLoading && !organization?.ssoEnabled && usersFlow && !hideForceAcceptance;
    const [requireAcceptance, setRequireAcceptance] = useState<boolean>(true);

    const {
        register,
        handleSubmit,
        getValues,
        setValue,
        setError,
        reset,
        watch,
        formState: { errors, isValid }
    } = useForm<IInvitationFormValues>({ mode: "onChange" });

    watch("firstName");
    watch("lastName");

    const [saving, setSaving] = useState(false);
    const [inviteAnother, setInviteAnother] = useState(false);
    const isProductAccessFlow = pathname.includes("/access/");

    const createNewInvitation = () => {
        const { firstName, lastName } = getValues();
        const selectedRoleGroups = toSelectedUserGroups({ accessDetailsByProductId, groupsWithoutProductAccess });

        if (selectedRoleGroups?.length && email) {
            return new Invitation({
                firstName,
                lastName,
                email: email,
                requireAcceptance: showReqAcceptanceChkBox ? requireAcceptance : false,
                organizationId: organizationId!,
                userGroups: selectedRoleGroups?.map((g) => ({ id: g.id!, name: g.name! })),
                createdBy: profile?.email!,
                created: new Date(),
                modified: new Date()
            });
        }

        return null;
    };

    const handleError = (apiErrors: IApiError[] | Error) => {
        if (Array.isArray(apiErrors)) {
            const errors = adaptApiErrors(apiErrors);
            errors.forEach((error) => {
                setError(error.name as any, {
                    type: "individualFieldApiError",
                    message: error.message
                });
            });
        } else {
            setError("apiError", {
                type: "apiError",
                message: INVITATION_ERROR_MESSAGE
            });
        }
    };

    const handleFormSubmission = async () => {
        const newInvitation = createNewInvitation();
        const selectedRoleGroups = toSelectedUserGroups({ accessDetailsByProductId, groupsWithoutProductAccess });
        setSaving(true);

        try {
            const success = await onCreate({ newInvitation, inviteAnother });

            if (success) {
                const [userGroupIds, userGroupNames, projectIds, projectNames] = selectedRoleGroups?.reduce(
                    (acc: [string[], string[], string[], string[]], group: Partial<UserGroupWithProjectDetails>) => {
                        acc[0].push(group.id!);
                        acc[1].push(group.name!);
                        if (group.projectName) {
                            acc[2].push(group.projectId!);
                            acc[3].push(group.projectName!);
                        } else if (group.projectIds?.length && group.projectNames?.length) {
                            acc[2].push(...group.projectIds);
                            acc[3].push(...group.projectNames);
                        }

                        return acc;
                    },
                    [[], [], [], []]
                );
                const payload = getGrantPermissionsPayload({
                    component: ANALYTICS_TRACKED_COMPONENTS.INVITATION_SIDEBAR,
                    userGroupIds,
                    userGroupNames,
                    email: newInvitation?.email!,
                    projectIds,
                    projectNames,
                    flow: isProductAccessFlow ? ANALYTICS_FLOWS.PRODUCT_ACCESS : ANALYTICS_FLOWS.USER_PAGE
                });
                sendTrackEvent(payload);

                // reset form values if invite another is checked
                if (inviteAnother) {
                    setInviteAnotherAccess(structuredClone(accessDetailsByProductId));
                    setInviteAnotherGroupsWithoutAccess(structuredClone(groupsWithoutProductAccess));
                    setSelectedUser(undefined);
                    setEmail(null);
                    reset();
                }
                emitToast({ message: INVITATION_SUCCESS_MESSAGE });
                window.dispatchEvent(new CustomEvent(REFETCH_PERMISSIONS_EVENT_NAME));
            }
        } catch (error) {
            handleError(error as IApiError[] | Error);
        } finally {
            setSaving(false);
        }
    };
    useEffect(() => {
        if (email !== selectedUser?.email) {
            // Always clear name fields when email changes
            setValue("firstName", "", { shouldValidate: false });
            setValue("lastName", "", { shouldValidate: false });

            // Only clear selectedUser and access when transitioning away from an existing user
            // (not when editing email for new users where selectedUser is undefined)
            if (selectedUser) {
                setSelectedUser(undefined);
                setAccessDetailsByProductId({});
            }
        }
    }, [email, selectedUser, setValue]);

    /* istanbul ignore next */
    const emailError = email === null || isValidEmail(email) ? "" : "Please enter a valid email";
    /* istanbul ignore next */
    const isSaveDisabled =
        (Object.keys(accessDetailsByProductId).length === 0 && groupsWithoutProductAccess.length === 0) ||
        !isValid ||
        !email ||
        !isValidEmail(email);

    return (
        <form
            className="flex flex--column user-form"
            onSubmit={handleSubmit(handleFormSubmission)}
            data-cy="invitation-form"
        >
            {errors.apiError && (
                <Attention alignment="left" className="push--top push-quad--bottom push-quad--sides" type="bad-news">
                    {errors.apiError.message}
                </Attention>
            )}
            <div className={styles["invitation-details__content"]}>
                <EmailSelectorInput
                    email={email}
                    emailError={emailError}
                    onSelectUser={async (user) => {
                        if (user?.email) {
                            setSelectedUser(user);

                            setValue("firstName", user.firstName, { shouldValidate: true });
                            setValue("lastName", user.lastName, { shouldValidate: true });
                            setEmail(user.email);
                        } else if (selectedUser) {
                            // Clear selectedUser if undefined is passed (e.g., disabled list item clicked)
                            // to prevent inconsistent state where name inputs are disabled but blank
                            setSelectedUser(undefined);
                        }
                    }}
                    onChange={async (email) => {
                        setEmail(email);
                    }}
                />
                <div className={`push-double--bottom soft-quad--sides ${errors.firstName && "oui-form-bad-news"}`}>
                    <label className="oui-label" htmlFor="invitation-first-name">
                        First Name
                        <span aria-label="(required)" className="oui-label--required" />
                    </label>

                    <input
                        aria-describedby="invitation-first-name-error"
                        className="oui-text-input"
                        id="invitation-first-name"
                        type="text"
                        disabled={!!selectedUser || !email || !!emailError}
                        {...register("firstName", { required: true })}
                    />
                    {errors.firstName && (
                        <span className="oui-form-note" id="invitation-first-name-error">
                            First name is required
                        </span>
                    )}
                </div>

                <div className={`push-quad--bottom soft-quad--sides ${errors.lastName && "oui-form-bad-news"}`}>
                    <label className="oui-label" htmlFor="invitation-last-name">
                        Last Name
                        <span aria-label="(required)" className="oui-label--required" />
                    </label>

                    <input
                        aria-describedby="invitation-last-name-error"
                        className="oui-text-input"
                        id="invitation-last-name"
                        type="text"
                        disabled={!!selectedUser || !email || !!emailError}
                        {...register("lastName", { required: true })}
                    />
                    {errors.lastName && (
                        <span className="oui-form-note" id="invitation-last-name-error">
                            Last name is required
                        </span>
                    )}
                </div>

                {showReqAcceptanceChkBox && (
                    <div className="push-quad--bottom soft-quad--sides">
                        <Checkbox
                            checked={requireAcceptance}
                            label="Require Acceptance"
                            data-cy="require-acceptance-checkbox"
                            description="The user must accept the invitation sent to their inbox"
                            onChange={() => setRequireAcceptance(!requireAcceptance)}
                        />
                    </div>
                )}
                <InvitationAccessForm
                    key={`${email}-${selectedUser?.id || "new"}`}
                    className="push-quad--bottom soft-quad--sides"
                    email={isValidEmail(email || "") ? email : ""}
                    organizationId={organizationId}
                    selectedUser={selectedUser}
                    onAccessDetailsChange={setAccessDetailsByProductId}
                    onGroupsWithoutProductAccessChange={setGroupsWithoutProductAccess}
                    initialAccessDetails={inviteAnotherAccess}
                    initialGroupsWithoutProductAccess={inviteAnotherGroupsWithoutAccess}
                />
            </div>
            <div className="flex">
                <SidebarFooter
                    onCancel={onCancel}
                    leftSide={[
                        <Checkbox
                            key="invite-another"
                            label="Invite another"
                            checked={inviteAnother}
                            onChange={(event) => {
                                setInviteAnother(event.target.checked);
                            }}
                            data-cy="invite-another-checkbox"
                        />
                    ]}
                >
                    <Button
                        key="send-invitation-button"
                        className=""
                        isLoading={saving}
                        loadingText="Saving"
                        style="highlight"
                        isSubmit
                        isDisabled={isSaveDisabled}
                    >
                        Send
                    </Button>
                </SidebarFooter>
            </div>
        </form>
    );
};

AccessManagementInvitationForm.displayName = "AccessManagementInvitationForm";
